import type { BusinessOrderAddress, BusinessOrderGeoAddress } from '~/types/business-order'

function parseDate(value: unknown): Date | null {
    if (value == null || value === '') {
        return null
    }
    const d = value instanceof Date
        ? value
        : new Date(typeof value === 'string' || typeof value === 'number' ? value : NaN)
    return Number.isNaN(d.getTime()) ? null : d
}

export function formatVisitTime(value: unknown): string {
    const d = parseDate(value)
    if (!d) {
        return '—'
    }
    const day = d.getDate()
    const month = d.toLocaleDateString('en-GB', { month: 'long' })
    const year = d.getFullYear()
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${day} ${month} ${year} ${h}:${min}`
}

export function formatVisitTimeShort(value: unknown): string {
    const d = parseDate(value)
    if (!d) {
        return '—'
    }
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    const day = d.getDate()
    const month = d.toLocaleDateString('en-GB', { month: 'long' })
    return `${h}:${min} – ${day} ${month}`
}

export function formatOrderCardDate(value: unknown): string {
    const d = parseDate(value)
    if (!d) {
        return '—'
    }
    const day = d.getDate()
    const month = d.toLocaleDateString('en-GB', { month: 'short' })
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${day} ${month} – ${h}:${min}`
}

export function formatOrderTotalDuration(raw: unknown): string | null {
    if (raw == null || String(raw).trim() === '') {
        return null
    }
    const s = String(raw).trim()
    const parts = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(s)
    if (!parts) {
        return s
    }
    const h = Number(parts[1])
    const min = Number(parts[2])
    if (h > 0 && min > 0) {
        return `${h}H ${min}M`
    }
    if (h > 0) {
        return `${h}H`
    }
    if (min > 0) {
        return `${min}M`
    }
    return null
}

export function formatAed(amount: unknown, withSuffix = true): string | null {
    if (amount == null || amount === '') {
        return null
    }
    const n = Number(amount)
    if (!Number.isFinite(n)) {
        return null
    }
    const formatted = n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2).replace(/\.?0+$/, '')
    return withSuffix ? `${formatted} AED` : formatted
}

export function formatOrderId(id: unknown): string {
    if (id == null || id === '') {
        return '—'
    }
    return `№${String(id)}`
}

export function getBusinessOrderDisplayId(
    record: { orderId?: string | number; id?: string | number } | null | undefined,
): string | number | null {
    if (!record) {
        return null
    }
    if (record.orderId != null && String(record.orderId).trim() !== '') {
        return record.orderId
    }
    const id = record.id
    if (typeof id === 'number') {
        return id
    }
    if (typeof id === 'string' && /^\d+$/.test(id.trim())) {
        return id.trim()
    }
    return null
}

export function getBusinessOrderKey(order: { documentId?: string; id?: string | number }): string {
    if (order.documentId) {
        return order.documentId
    }
    return String(order.id)
}

export function getBusinessOrderOfferKey(offer: { documentId?: string; id?: string | number }): string {
    if (offer.documentId) {
        return offer.documentId
    }
    return String(offer.id)
}

export function parseOfferCountdown(offer: { pendingUntil?: string; createdAt?: string }): {
    deadline: Date | null
    startedAt: Date | null
} {
    return {
        deadline: parseDate(offer.pendingUntil),
        startedAt: parseDate(offer.createdAt),
    }
}

export function resolveBusinessOrderAddressText(
    address: string | BusinessOrderAddress | BusinessOrderGeoAddress | null | undefined,
): string {
    if (address == null) {
        return ''
    }
    if (typeof address === 'string') {
        return address.trim()
    }
    if (typeof address === 'object') {
        if ('location' in address && address.location?.address?.trim()) {
            return address.location.address.trim()
        }
        if ('address' in address && typeof address.address === 'string') {
            return address.address.trim()
        }
    }
    return ''
}

export interface BusinessOrderAddressDetailLabels {
    entrance: string
    floor: string
    unit: string
}

export function formatBusinessOrderAddressDetails(
    address: string | BusinessOrderAddress | null | undefined,
    labels: BusinessOrderAddressDetailLabels,
): string {
    if (address == null || typeof address === 'string') {
        return ''
    }
    const parts: string[] = []
    const entrance = address.entrance?.trim()
    if (entrance) {
        parts.push(`${labels.entrance} ${entrance}`)
    }
    const floor = address.floor?.trim()
    if (floor) {
        parts.push(`${labels.floor} ${floor}`)
    }
    const unit = address.unit?.trim()
    if (unit) {
        parts.push(`${labels.unit} ${unit}`)
    }
    const description = address.description?.trim()
    if (description) {
        parts.push(description)
    }
    return parts.join(' · ')
}

export function getBusinessOrderMapCoordinates(
    record: { address?: string | BusinessOrderAddress | null } | null | undefined,
): { lat: number; lng: number } | null {
    if (!record?.address || typeof record.address === 'string') {
        return null
    }
    const geo = record.address.location
    if (!geo) {
        return null
    }
    const lat = geo.lat
    const lng = geo.lng
    if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null
    }
    return { lat, lng }
}

export function formatBusinessOrderLocation(
    record: {
        address?: string | BusinessOrderAddress | BusinessOrderGeoAddress | null
        location?: { name?: string | null } | null
    } | null | undefined,
): string {
    if (!record) {
        return '—'
    }
    const address = resolveBusinessOrderAddressText(record.address)
    const areaName = record.location?.name?.trim() ?? ''
    if (address && areaName) {
        return `${address}, ${areaName}`
    }
    if (address) {
        return address
    }
    if (areaName) {
        return areaName
    }
    return '—'
}

export function formatCustomerLabel(
    customer: { name?: string; username?: string; email?: string } | null | undefined,
): string {
    if (!customer) {
        return '—'
    }
    const name = customer.name?.trim()
    if (name) {
        return name
    }
    const username = customer.username?.trim()
    if (username) {
        return username
    }
    const email = customer.email?.trim()
    if (email) {
        return email
    }
    return '—'
}

export function getOrderCountdownStart(order: { publishedAt?: string; createdAt?: string }): Date | null {
    return parseDate(order.publishedAt ?? order.createdAt)
}

export function getBusinessOrderExecutionKey(
    execution: { documentId?: string; id?: string | number },
): string {
    if (execution.documentId) {
        return execution.documentId
    }
    return String(execution.id)
}

export function formatExecutionCardSchedule(start: unknown, finish: unknown): string {
    const startDate = parseDate(start)
    if (!startDate) {
        return '—'
    }
    const day = startDate.getDate()
    const month = startDate.toLocaleDateString('en-GB', { month: 'long' })
    const startTime = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
    const finishDate = parseDate(finish)
    if (!finishDate) {
        return `${day} ${month} | ${startTime}`
    }
    const finishTime = `${String(finishDate.getHours()).padStart(2, '0')}:${String(finishDate.getMinutes()).padStart(2, '0')}`
    return `${day} ${month} | ${startTime} – ${finishTime}`
}

export function formatCustomerInitial(
    customer: { name?: string; username?: string; email?: string } | null | undefined,
): string {
    const label = formatCustomerLabel(customer)
    const first = label.trim().charAt(0)
    return first && first !== '—' ? first.toUpperCase() : '—'
}

export function formatCustomerLocationLine(
    order: {
        customer?: { name?: string; username?: string; email?: string } | null
        address?: string | BusinessOrderAddress | BusinessOrderGeoAddress | null
        location?: { name?: string | null } | null
    } | null | undefined,
): string {
    if (!order) {
        return '—'
    }
    const name = formatCustomerLabel(order.customer)
    const location = formatBusinessOrderLocation(order)
    if (name !== '—' && location !== '—') {
        return `${name} • ${location}`
    }
    if (name !== '—') {
        return name
    }
    return location
}

